import React from "react";
import { isFinnishMarket } from "@kivra/sdk/common";
import { getFocusStyle } from "../../theme";
import { styled, useTheme } from "../../theme/styled";
import { font } from "../typography/baseStyling";
import { Flex } from "../util/Flex";

export type LogoTextPositions = "bottom" | "right";

interface Props {
  className?: string;
  size: "small" | "medium" | "large";
  text?: string;
  textPosition?: LogoTextPositions;
  onClick?: (
    e: React.KeyboardEvent<Element> | React.MouseEvent<Element, MouseEvent>,
  ) => unknown;
  href: string;
  dataTestId?: string;
}

const LogoImage = ({
  className,
  size,
}: Pick<Props, "className" | "size">): React.JSX.Element => {
  const themeName: "light" | "dark" = useTheme().themeName;
  const logoDomain = isFinnishMarket() ? "static.kivra.fi" : "static.kivra.com";
  const logoUrl =
    `https://${logoDomain}/assets/logo/kivra_logo_${themeName}.svg`;

  const getLogoHeight = (size: Props["size"]): number => {
    if (size === "small") {
      return 20;
    } else if (size === "medium") {
      return 28;
    }
    return 32;
  };

  return (
    <Img
      src={logoUrl}
      alt="Kivra logo"
      className={className}
      height={getLogoHeight(size)}
    />
  );
};

const Img = styled.img({
  alignSelf: "flex-start",
});

const Logo = ({
  text,
  textPosition,
  className,
  size,
}: Props): React.JSX.Element => {
  return text
    ? (
      <>
        <LogoImage className={className} size={size} />
        <Text textPosition={textPosition} size={size}>
          {text}
        </Text>
      </>
    )
    : <LogoImage className={className} size={size} />;
};

export const GreenLogo = (props: Props): React.JSX.Element => {
  return props.onClick || props.href
    ? (
      <StyledLink
        onClick={props.onClick
          ? (event) => {
            event.preventDefault();
            props.onClick?.(event);
          }
          : undefined}
        href={props.href}
        rel="noreferrer noopener"
        textPosition={props.textPosition}
        data-test-id={props.dataTestId}
      >
        <Logo {...props} />
      </StyledLink>
    )
    : (
      <Flex
        alignItems={props.textPosition === "bottom" ? undefined : "center"}
        direction={props.textPosition === "bottom" ? "column" : "row"}
      >
        <Logo {...props} />
      </Flex>
    );
};

const Text = styled("span")<Pick<Props, "textPosition" | "size">>((p) => {
  const sizeStyles = {
    small: {
      ...font.HeadingXS,
    },
    medium: {
      ...font.HeadingS,
    },
    large: {
      ...font.HeadingM,
    },
  } as const;
  return {
    fontFamily: "$heading-font",
    color: "$text-logo",
    marginLeft: p.textPosition === "right" ? "$spacing-8" : undefined,
    whiteSpace: "nowrap",
    ...sizeStyles[p.size],
  };
});

const StyledLink = styled.a<Pick<Props, "textPosition">>((p) => ({
  display: "flex",
  alignItems: p.textPosition === "bottom" ? undefined : "center",
  flexDirection: p.textPosition === "bottom" ? "column" : "row",
  textDecoration: "none",
  ...getFocusStyle(),
}));
